import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import MainLayoutApple from '../components/apple-ui/MainLayoutApple';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function AffiliatePage(props) {
  const [dashboard, setDashboard] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const { isDarkMode } = useTheme();

  const cardBg = isDarkMode ? '#111827' : '#fff';
  const cardBorder = isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb';
  const textPrimary = isDarkMode ? '#f1f5f9' : '#111827';
  const textSecondary = isDarkMode ? '#94a3b8' : '#6b7280';
  const textMuted = isDarkMode ? '#64748b' : '#9ca3af';
  const textCard = isDarkMode ? '#f1f5f9' : '#374151';
  const rowBorder = isDarkMode ? '1px solid #1e293b' : '1px solid #f3f4f6';
  const tableBorder = isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb';
  const tdText = isDarkMode ? '#e2e8f0' : '#374151';
  const refLinkBg = isDarkMode ? '#1e293b' : '#f0f4ff';
  const refLinkColor = isDarkMode ? '#93c5fd' : '#1e40af';
  const payoutBg = isDarkMode ? '#1e293b' : '#f0f4ff';
  const payoutBorder = isDarkMode ? '1px solid #1e40af' : '1px solid #bfdbfe';
  const payoutColor = isDarkMode ? '#93c5fd' : '#1e40af';
  const linkColor = isDarkMode ? '#60a5fa' : '#1d4ed8';

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashRes, usersRes] = await Promise.all([
        fetch(`${API_BASE}/api/affiliate/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/affiliate/referred-users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!dashRes.ok || !usersRes.ok) { setError('Failed to load referral data.'); return; }
      const dashData = await dashRes.json();
      const usersData = await usersRes.json();
      setDashboard(dashData);
      setReferredUsers(usersData.referred_users || []);
    } catch {
      setError('Failed to load referral data.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyLink = () => {
    if (!dashboard?.ref_link) return;
    navigator.clipboard.writeText(dashboard.ref_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <MainLayoutApple {...props}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div style={{ color: textSecondary }}>Loading referral data...</div>
        </div>
      </MainLayoutApple>
    );
  }

  if (error) {
    return (
      <MainLayoutApple {...props}>
        <div style={{ padding: '2rem', color: '#dc2626' }}>{error}</div>
      </MainLayoutApple>
    );
  }

  return (
    <MainLayoutApple {...props}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem', fontFamily: 'inherit' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: textPrimary, marginBottom: '0.5rem' }}>
        Referral Program
      </h1>
      <p style={{ color: textSecondary, marginBottom: '2rem' }}>
        Earn commissions when your referrals subscribe or add wallet credits.
      </p>

      {/* Referral Link */}
      <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: textCard, marginBottom: '0.75rem' }}>
          Your Referral Link
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            flex: 1, background: refLinkBg,
            border: `1px solid ${isDarkMode ? '#1e40af' : '#2563eb33'}`,
            borderRadius: '8px', padding: '0.625rem 1rem', fontSize: '0.9rem',
            color: refLinkColor, fontFamily: 'monospace', wordBreak: 'break-all',
          }}>
            {dashboard?.ref_link}
          </div>
          <button onClick={copyLink} style={{
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px',
            padding: '0.625rem 1.25rem', fontWeight: 600, cursor: 'pointer',
            fontSize: '0.9rem', whiteSpace: 'nowrap',
          }}>
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <p style={{ fontSize: '0.8rem', color: textMuted, marginTop: '0.5rem' }}>
          Code: <strong>{dashboard?.ref_code}</strong>
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Earned', value: `₹${(dashboard?.total_earned || 0).toFixed(0)}`, color: '#059669' },
          { label: 'Pending Payout', value: `₹${(dashboard?.total_pending || 0).toFixed(0)}`, color: '#d97706' },
          { label: 'Total Paid Out', value: `₹${(dashboard?.total_paid || 0).toFixed(0)}`, color: '#2563eb' },
          { label: 'Referred Users', value: dashboard?.referred_count || 0, color: '#7c3aed' },
        ].map((stat) => (
          <div key={stat.label} style={{ background: cardBg, border: cardBorder, borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '0.8rem', color: textSecondary, marginTop: '0.25rem' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Commission structure */}
      <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: textCard, marginBottom: '1rem' }}>
          Commission Structure
        </h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: tableBorder }}>
              <th style={{ textAlign: 'left', padding: '0.5rem 0', color: textSecondary, fontWeight: 500 }}>Event</th>
              <th style={{ textAlign: 'right', padding: '0.5rem 0', color: textSecondary, fontWeight: 500 }}>You earn</th>
            </tr>
          </thead>
          <tbody>
            {[
              { event: 'Referred user buys Starter plan', earn: '₹200 (one-time)' },
              { event: 'Referred user buys Professional plan', earn: '₹500 (one-time)' },
              { event: 'Referred user buys Business plan', earn: '₹1,000 (one-time)' },
              { event: 'Referred user tops up wallet', earn: '10% of amount' },
            ].map((row) => (
              <tr key={row.event} style={{ borderBottom: rowBorder }}>
                <td style={{ padding: '0.625rem 0', color: tdText }}>{row.event}</td>
                <td style={{ padding: '0.625rem 0', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{row.earn}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Referred Users */}
      {referredUsers.length > 0 && (
        <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: textCard, marginBottom: '1rem' }}>
            Referred Users ({referredUsers.length})
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: tableBorder }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Plan</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {referredUsers.map((u, i) => (
                  <tr key={i} style={{ borderBottom: rowBorder }}>
                    <td style={{ padding: '0.625rem 0.75rem', color: tdText }}>{u.email}</td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <span style={{
                        background: u.plan === 'free'
                          ? (isDarkMode ? '#1e293b' : '#f3f4f6')
                          : (isDarkMode ? '#1e3a5f' : '#eff6ff'),
                        color: u.plan === 'free'
                          ? (isDarkMode ? '#94a3b8' : '#6b7280')
                          : (isDarkMode ? '#93c5fd' : '#1d4ed8'),
                        borderRadius: '4px', padding: '0.125rem 0.5rem',
                        fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize',
                      }}>
                        {u.plan}
                      </span>
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem', color: textMuted }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commission History */}
      {dashboard?.commissions?.length > 0 && (
        <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: textCard, marginBottom: '1rem' }}>
            Commission History
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: tableBorder }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Type</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Commission</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: textSecondary, fontWeight: 500 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.commissions.map((c) => (
                  <tr key={c.id} style={{ borderBottom: rowBorder }}>
                    <td style={{ padding: '0.625rem 0.75rem', color: textMuted }}>
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem', color: tdText, textTransform: 'capitalize' }}>
                      {c.commission_type === 'plan' ? `${c.plan_id} plan` : 'Wallet topup'}
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                      ₹{c.commission_amount}
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <span style={{
                        background: c.status === 'paid'
                          ? (isDarkMode ? '#064e3b' : '#d1fae5')
                          : (isDarkMode ? '#451a03' : '#fef3c7'),
                        color: c.status === 'paid'
                          ? (isDarkMode ? '#6ee7b7' : '#065f46')
                          : (isDarkMode ? '#fcd34d' : '#92400e'),
                        borderRadius: '4px', padding: '0.125rem 0.5rem',
                        fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize',
                      }}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payout Info */}
      <div style={{
        background: payoutBg, border: payoutBorder, borderRadius: '12px',
        padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: payoutColor,
      }}>
        <strong>Payout process:</strong> Commissions are paid manually via UPI or bank transfer.
        Once your pending balance reaches ₹500, contact us at{' '}
        <a href="mailto:support@t09n.com" style={{ color: linkColor }}>support@t09n.com</a>{' '}
        with your UPI ID or bank details. We process payouts within 7 business days.
      </div>
    </div>
    </MainLayoutApple>
  );
}

export default AffiliatePage;
